import { Badge } from '@mantine/core';
import { IconChartLine } from '@tabler/icons-react';

import type { FetchTimeseriesDatapointsResult } from './schema.ts';
import type { FetchTimeseriesDatapointsUiArgs } from './types.ts';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';
import { ViewExternalIdBadgeList } from '#plugin-chat/components/tool-display/ViewExternalIdBadge.tsx';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';
import type { ToolProgressComponent } from '#plugin-chat/lib/tool/progress-registry.ts';

/**
 * UI component for FetchTimeseriesDatapointsTool.
 */
export function RenderFetchTimeseriesDatapointsTool() {
  const toolPart = useToolPart<FetchTimeseriesDatapointsUiArgs, FetchTimeseriesDatapointsResult>();
  const inputDataset = toolPart.args?.inputDataset;
  const externalIds = toolPart.args?.externalIds ?? [];

  // Build display info for running state
  const getDisplayInfo = () => {
    if (inputDataset) {
      return inputDataset;
    }
    if (externalIds.length > 2) {
      return `${externalIds.slice(0, 2).join(', ')} +${externalIds.length - 2} more`;
    }
    return externalIds.join(', ');
  };
  const displayInfo = getDisplayInfo();

  return (
    <ToolDisplay>
      <ToolDisplay.Running>
        <span>Fetching timeseries datapoints</span>
        {displayInfo && (
          <Badge color="dark.1" size="xs" variant="outline">
            {inputDataset ? `from ${inputDataset}` : displayInfo}
          </Badge>
        )}
      </ToolDisplay.Running>
      <ToolDisplay.Complete color="var(--mantine-color-cyan-4)" icon={IconChartLine}>
        <div>
          <span>Timeseries datapoints fetched</span>
          <ViewExternalIdBadgeList externalIds={inputDataset ? [`from ${inputDataset}`] : externalIds} />
        </div>
      </ToolDisplay.Complete>
      <ToolDisplay.Error>Failed to fetch datapoints</ToolDisplay.Error>
    </ToolDisplay>
  );
}
/**
 * Progress component for FetchTimeseriesDatapointsTool.
 *
 * Rendered inside the "AiPSO Agent is ..." progress bubble while the tool is running.
 */
export const ProgressFetchTimeseriesDatapointsTool: ToolProgressComponent<
  FetchTimeseriesDatapointsUiArgs,
  FetchTimeseriesDatapointsResult
> = ({ args }) => {
  const count = args?.externalIds?.length ?? 0;
  if (count === 0) {
    return <>fetching datapoints...</>;
  }
  return <>fetching datapoints from {count} timeseries...</>;
};
